import { RouteComponentProps } from 'react-router'
import { API } from '../../utils/api'
import { css } from '@emotion/core'
import { FormikActions } from 'formik'
import * as Yup from 'yup'
import { Button, colors, media, Modal, Spinner, Typography } from '@dw/helix'
import React, { useContext, useEffect, useState } from 'react'
import { SerialTracingForm, TraceFormValues } from './SerialTracingForm'
import { SerialResults } from './SerialResults'
import { OrderResults } from './OrderResults'
import { AuthContext, UserGroup } from '../../contexts'
import { ParsedTrace, Trace, TracesData } from '../../types/DataTypes'

export interface SerialTracingPageRouteParams {
  type?: string
  id?: string
}

interface Serial {
  serialNo: string
  batchNo: string
  hashedSerial: string
  articleNo?: string
  orderNo?: string
}

const wrapperStyles = css`
  display: flex;
  margin-bottom: 20px;
`

const tableContainerStyles = css`
  width: 100%;
`
const modalButtonsStyles = css`
  margin-top: 42px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  & > button {
    flex: 1;
    &:first-of-type {
      margin-right: 20px;
    }
  }
  ${media.MEDIA_MAX_SMALL} {
    flex-direction: column;
    & > button {
      width: 170px;
      &:first-of-type {
        margin-right: 0;
        margin-bottom: 12px;
      }
    }
  }
`

const modalContentStyles = css`
  padding-left: 78px;
  padding-right: 78px;
  ${media.MEDIA_MAX_SMALL} {
    padding-left: 20px;
    padding-right: 20px;
  }
`

export const parseTraces = (traces: Trace[]): ParsedTrace[] =>
  traces.map(trace => ({
    created: trace.Created,
    productId: trace.ProductId,
    companySupplyChainRole: trace.CompanySupplyChainRole,
    orderNo: trace.OrderNo,
    ip: trace.IP,
    serialNo: trace.serialNo,
    companyName: trace['Company Name'],
    hashedSerial: trace.hashedSerial,
    userName: trace['User Name'],
    userId: trace.UserId,
    traceTime: trace.TraceTime,
    userLocation: trace.UserLocation,
    traceEvent: trace.TraceEvent,
    id: trace.id,
  }))

export const SerialTracing: React.FunctionComponent<RouteComponentProps<SerialTracingPageRouteParams>> = props => {
  const { hasGroup } = useContext(AuthContext)

  const [traceFormInitialValues, setTraceFormInitialValues] = useState<TraceFormValues>({
    'trace-event': props.match.params.type || 'serial',
    serial: props.match.params.id || '',
  })
  const [isFetchingData, setIsFetchingData] = useState<boolean>(false)
  const [deleting, setDeleting] = useState<boolean>(false)
  const [error, setError] = useState<boolean | undefined>()
  const [traces, setTraces] = useState<ParsedTrace[] | null>(null)
  const [traceEvent, setTraceEvent] = useState<string>(traceFormInitialValues['trace-event'])
  const [batchNumber, setBatchNumber] = useState<string>('')
  const [scannedCount, setScannedCount] = useState<number>(0)
  const [isModalOpen, toggleModal] = useState<boolean>(false)

  const searchTypes: { [key: string]: string } = {
    serial: 'Serial number',
    order: 'Order number',
    ...(hasGroup([UserGroup.Admin, UserGroup.SuperAdmin]) && {
      'hashed-serial': 'Hashed serial number',
      id: 'ID number',
    }),
  }

  const traceFormValidationSchema = Yup.object().shape({
    'trace-event': Yup.string()
      .required('Trace Event is required')
      .oneOf(Object.keys(searchTypes), 'Trace Event is not valid'),
    serial: Yup.string().required(`${searchTypes[traceEvent]} is required`),
  })

  useEffect(() => {
    const checkMatchParams = () => {
      if (!props.match) return
      const {
        params: { type, id },
      } = props.match

      if (!type && !id) return

      const traceInitialValues = { 'trace-event': type, serial: id }

      if (!traceFormValidationSchema.isValidSync(traceInitialValues)) {
        props.history.push('/trace')
        return
      }
      setTraceFormInitialValues(traceInitialValues)
    }

    checkMatchParams()

    return () => setError(undefined)
  }, [props.match])

  useEffect(() => {
    const fetchSerialNumber = (serial: string): Promise<Serial> => {
      return API.get('its-serials', `/serials/serialNo/${serial}`, {}).catch(() => {
        return {
          serialNo: serial,
          batchNo: 'unknown',
          hashedSerial: 'unknown',
          articleNo: 'unknown',
          orderNo: 'unknown',
        }
      })
    }
    const fetchTraces = (traceType: string, serial: string): Promise<ParsedTrace[]> => {
      return new Promise((resolve, reject) => {
        const allTraces: Trace[] = []
        let count: number

        const fetchFromApi = async (lastEvaluatedKeyId?: string) => {
          try {
            const data: TracesData = await API.get('its', `/tracing/${traceType}`, {
              queryStringParameters: {
                searchParam: serial,
                ...(lastEvaluatedKeyId && { lastKey: lastEvaluatedKeyId }),
              },
            })

            if (typeof data.ScannedCount === 'number') count = data.ScannedCount

            allTraces.push(...data.Items)

            if (data.LastEvaluatedKey) {
              await fetchFromApi(data.LastEvaluatedKey.id)
              return
            }
            setScannedCount(count)
            resolve(parseTraces(allTraces))
          } catch (err) {
            setError(true)
            reject(err)
          }
        }

        fetchFromApi()
      })
    }
    const trace = async (traceFormValues: TraceFormValues): Promise<ParsedTrace[]> => {
      setIsFetchingData(true)

      try {
        const inputValue = traceFormValues.serial
        const traceByType = traceFormValues['trace-event']
        const fieldValue = traceByType === 'order' ? String(inputValue).toUpperCase() : inputValue
        return await fetchTraces(traceByType, fieldValue)
      } finally {
        setIsFetchingData(false)
      }
    }

    const fetch = async () => {
      if (traceFormInitialValues['trace-event'] && traceFormInitialValues.serial) {
        const fetchedTraces = await trace(traceFormInitialValues)

        if (!fetchedTraces.length || traceFormInitialValues['trace-event'] === 'order') {
          setTraces(fetchedTraces)
          return
        }

        const serialResponse: Serial = await fetchSerialNumber(fetchedTraces[0].serialNo)

        setTraces(fetchedTraces)

        setBatchNumber(serialResponse.batchNo)
        return
      }
    }

    fetch()
  }, [traceFormInitialValues])

  const handleSubmit = (values: TraceFormValues, actions: FormikActions<TraceFormValues>): void => {
    props.history.push(`/trace/${values['trace-event']}/${values.serial}`)
    setTraces(null)
    actions.setSubmitting(false)
  }

  const handleDeleteOrder = async () => {
    setDeleting(true)
    const response = await API.del('its', `/trace-event/${traceFormInitialValues.serial}`, {})
    setTraces([])
    setDeleting(false)
    return response
  }

  const openTraceEvent = (newTraceEvent: string) => {
    setTraceEvent(newTraceEvent)
    setTraces(null)
    setTraceFormInitialValues({ 'trace-event': newTraceEvent, serial: '' })
    props.history.push(`/trace`, {})
  }

  return (
    <div css={wrapperStyles}>
      <SerialTracingForm
        searchTypes={searchTypes}
        traceFormInitialValues={traceFormInitialValues}
        traceFormValidationSchema={traceFormValidationSchema}
        isFetchingData={isFetchingData}
        onSubmit={handleSubmit}
        onTraceEventChange={openTraceEvent}
      />
      <div css={tableContainerStyles}>
        {traceEvent === 'order' ? (
          <OrderResults
            traces={traces}
            error={error}
            orderNumber={traceFormInitialValues.serial}
            canAccessOrderResults={hasGroup([UserGroup.Admin, UserGroup.SuperAdmin])}
            orderHasBeenDeleted={traces !== null && traces.length === 0 && scannedCount > 0}
            toggleModal={toggleModal}
          />
        ) : (
          <SerialResults
            traces={traces}
            error={error}
            batchNumber={batchNumber}
            canSeeLinkToBatch={hasGroup([UserGroup.Admin, UserGroup.SuperAdmin])}
            onTraceEventChange={openTraceEvent}
          />
        )}
      </div>
      {isModalOpen && (
        <Modal
          onClose={() => toggleModal(false)}
          title={`Delete order`}
          css={css`
            margin-top: 180px;
          `}
          contentCss={modalContentStyles}
        >
          {({ close }) => (
            <>
              <Typography type="snippet">Are you sure you want to delete this order?</Typography>
              <div css={modalButtonsStyles}>
                <Button theme="secondary" onClick={close}>
                  Go Back
                </Button>
                <Button
                  onClick={async () => {
                    await handleDeleteOrder()
                    close()
                  }}
                  disabled={deleting}
                >
                  {deleting ? (
                    <Spinner
                      color={colors.white}
                      css={css`
                        margin: -2px 0;
                      `}
                    />
                  ) : (
                    `Delete Order`
                  )}
                </Button>
              </div>
            </>
          )}
        </Modal>
      )}
    </div>
  )
}
